import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import MailIcon from "@/components/SVGComponents/MailIcon";
import ShieldIcon from "@/components/SVGComponents/ShieldIcon";
import { emailRegex } from "@/lib/regex";
import { captalizeEachWord, combineClasses, requiredMessage } from "@/lib/utils";
import toast from "@/lib/toast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getRoles } from "@/store/slices/roleSlice";
import { getAdminUsers } from "@/store/slices/userSlice";
import userService, { type User } from "@/services/api/user";
import styles from "./InviteAdminModal.module.scss";
const ADMIN_USERS_QUERY = { pageNo: 1, perPage: 10, role: "ADMIN", screenType: "management" };

interface SelectOption {
  label: string;
  value: string;
}
const ROLES_FETCH_PARAMS = { pageNo: 1, perPage: 100 };
const formatRoleName = (name: string) => captalizeEachWord(name.replace(/_/g, " ").toLowerCase());

interface InviteAdminFormValues {
  firstName: string;
  lastName: string;
  workEmail: string;
  roles: SelectOption[];
  mobileNo?: string;
}

export interface InviteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: User | null;
  onUpdated?: () => void;
}

const InviteAdminModal = ({ isOpen, onClose, admin, onUpdated }: InviteAdminModalProps) => {
  const dispatch = useAppDispatch();
  const isEditMode = Boolean(admin);
  const { roles, isFetching: isFetchingRoles } = useAppSelector((state) => state.role);

  const { register, handleSubmit, control, reset, formState } = useForm<InviteAdminFormValues>({
    defaultValues: { firstName: "", lastName: "", workEmail: "", roles: [], mobileNo: "" },
  });

  useEffect(() => {
    if (!isOpen) return;
    dispatch(getRoles(ROLES_FETCH_PARAMS));
  }, [dispatch, isOpen]);

  const roleOptions: SelectOption[] = useMemo(
    () => roles.map((role) => ({ label: formatRoleName(role.name), value: String(role.id) })),
    [roles],
  );

  // The admin's assigned roles (`userRoles` from `/admin-users`) as select options.
  const adminRoleOptions: SelectOption[] = useMemo(
    () =>
      (admin?.userRoles ?? []).map((role) => ({ label: formatRoleName(role.name), value: String(role.id) })),
    [admin],
  );

  useEffect(() => {
  if (!isOpen) return;

  if (admin) {
    reset({
      firstName: admin.firstName || "",
      lastName: admin.lastName || "",
      workEmail: admin.email || "",
      roles: adminRoleOptions,
      mobileNo: admin.mobileNo || "",
    });
  } else {
    reset({
      firstName: "",
      lastName: "",
      workEmail: "",
      roles: [],
      mobileNo: "",
    });
  }
}, [admin, adminRoleOptions, isOpen, reset]);

  const fields: FormConfig = useMemo(
    () => ({
      firstName: {
        controlType: "input",
        label: "First name",
        labelVariant: "caption",
        placeholder: "First name",
        validations: { required: requiredMessage("First name") },
        props: { className: combineClasses(styles.fieldInput) },
      },
      lastName: {
        controlType: "input",
        label: "Last name",
        labelVariant: "caption",
        placeholder: "Last name",
        validations: { required: requiredMessage("Last name") },
        props: { className: combineClasses(styles.fieldInput) },
      },
      workEmail: {
        controlType: "input",
        label: "Work email",
        labelVariant: "caption",
        placeholder: "name@thecharter.in",
        validations: {
          required: requiredMessage("Work email"),
          pattern: { value: emailRegex, message: "Enter a valid email" },
        },
        props: { className: combineClasses(styles.fieldInput) ,
          disabled: isEditMode,
        },
      },
      roles: {
        controlType: "select",
        label: "Role",
        labelVariant: "caption",
        isControlled: true,
        validations: {
          validate: (value: SelectOption[]) => (value && value.length > 0) || requiredMessage("Role"),
        },
        props: {
          options: roleOptions,
          isMulti: true,
          isSearchable: false,
          isLoading: isFetchingRoles && roleOptions.length === 0,
          className: combineClasses(styles.fieldSelect),
        },
      },
      mobileNo: {
        controlType: "input",
        label: "Mobile number",
        placeholder: "Mobile number",
        validations: {
          pattern: {
            value: /^[0-9]{10}$/,
            message: "Enter a valid 10-digit mobile number",
          },
        },
        props: { className: combineClasses(styles.fieldInput) },
      },
    }),
    [roleOptions, isFetchingRoles, isEditMode],
  );

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
  try {
    if (isEditMode && admin) {
      await userService.updateAdmin(admin.id, {
        firstName: values.firstName,
        lastName: values.lastName,
        mobileNo: values.mobileNo ?? "",
        roleIds: values.roles.map((role) => Number(role.value)),
      });
    } else {
      await userService.inviteAdmin({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.workEmail,
        mobileNo: values.mobileNo ?? "",
        roleIds: values.roles.map((role) => Number(role.value)),
      });
    }

    toast.success(
      isEditMode ? "Admin updated successfully." : "Invite sent successfully."
    );

    dispatch(getAdminUsers(ADMIN_USERS_QUERY));
    handleClose();
  } catch {
    // Error toast handled by interceptor
  }
});

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Admin" : "Invite an Admin"}
      subtitle={
  isEditMode
    ? "Update the admin's details."
    : "They will be asked to set a password."
}
      onSubmit={onSubmit}
      submitLabel={isEditMode ? "Save changes" : "Send invite"}
      submitIcon={
  isEditMode ? undefined : <MailIcon width={16} height={16} />
}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.inviteAdminModal)}
    >
      {formRenderer<InviteAdminFormValues>(fields, formState, register, control)}
    </FormModal>
  );
};

export default InviteAdminModal;
